/// <reference types="vite/client" />

/**
 * admin-client.ts — Sprint 23.5 (updated Sprint 24.4, Sprint 25.7)
 *
 * Fetch wrapper for the Lords of the Streets admin API.
 * All requests go through adminFetch which:
 *   - Prefixes the path with VITE_API_BASE_URL (prod) or '' (dev proxy)
 *   - Adds Authorization: Bearer <token> from localStorage
 *   - Parses JSON response
 *   - Redirects to /login on 401/403
 *   - Throws AdminApiError on non-2xx
 *
 * Environment variables (Vite):
 *   VITE_API_BASE_URL  — Absolute URL of the game server, no trailing slash.
 *                        Leave unset (or set to '') in local dev; Vite's proxy
 *                        (vite.config.ts) forwards /admin/* → localhost:4199.
 *                        Set to https://lords-of-the-streets-api.fly.dev for
 *                        production builds (see npm run build:prod).
 */

// Sprint 25.7: read the API origin from the Vite env var injected at build time.
// Falls back to '' so relative paths work through the Vite dev proxy unchanged.
const API_BASE: string = import.meta.env.VITE_API_BASE_URL ?? '';

import {
  adaptShardHealthResponse,
  adaptDeadLettersResponse,
  adaptAbuseSignalsResponse,
  adaptAccountSearchResponse,
} from './shape-adapters';

const TOKEN_KEY = 'admin_token';
const EMAIL_KEY = 'admin_email';
const ROLE_KEY  = 'admin_role';

export class AdminApiError extends Error {
  code: string;
  status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'AdminApiError';
    this.code = code;
    this.status = status;
  }
}

function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? '';
}

export function saveCredentials(token: string, email: string) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(EMAIL_KEY, email);
  // Role is populated after the first getMe() call; clear stale value on fresh login
  localStorage.removeItem(ROLE_KEY);
}

export function clearCredentials() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EMAIL_KEY);
  localStorage.removeItem(ROLE_KEY);
}

export function getAdminEmail(): string {
  return localStorage.getItem(EMAIL_KEY) ?? '';
}

/** Cached role from the last successful getMe() — may be '' before first call */
export function getCachedRole(): string {
  return localStorage.getItem(ROLE_KEY) ?? '';
}

export function setCachedRole(role: string) {
  localStorage.setItem(ROLE_KEY, role);
}

async function adminFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> ?? {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });

  // Auth failure → clear and redirect
  if (response.status === 401 || response.status === 403) {
    clearCredentials();
    window.location.href = '/login';
    // Throw so the caller's promise rejects cleanly
    throw new AdminApiError('unauthorized', 'Session expired — redirecting to login', response.status);
  }

  let body: any;
  try {
    body = await response.json();
  } catch {
    body = {};
  }

  if (!response.ok) {
    const err = body?.error ?? {};
    throw new AdminApiError(
      err.code ?? 'unknown_error',
      err.message ?? `HTTP ${response.status}`,
      response.status,
    );
  }

  return body as T;
}

// ─── Type definitions ─────────────────────────────────────────────────────────

export type AccountDetail = {
  account_id: string;
  email?: string;
  username?: string;
  admin_role?: string;
  created_at?: string;
  banned_at?: string;
  suspended_until?: string;
  suspension_reason?: string;
  session_count?: number;
  report_count?: number;
  warn_count?: number;
  warn_history?: WarnEntry[];
  tutorial_state?: string;
};

export type WarnEntry = {
  warn_id?: string;
  reason: string;
  created_at: string;
  actor_account_id?: string;
};

export type Report = {
  report_id: string;
  reporter_account_id: string;
  reported_account_id: string;
  category: string;
  reason: string;
  context?: string;
  status: string;
  created_at: string;
  resolved_at?: string;
  resolution_action?: string;
  resolution_reason?: string;
};

export type ReportListResponse = {
  reports: Report[];
  total: number;
  limit: number;
  offset: number;
};

export type ShardHealthSnapshot = {
  shard_id: string;
  snapshot_at: string;
  active_player_count: number;
  gang_count: number;
  last_event_at: string | null;
  total_cash_distributed: number;
  p50_crew: number;
  p50_cash: number;
  p50_blocks: number;
  p95_heat: number;
  error_rate_60s: number;
  raw_payload?: any;
};

export type DeadLetterEvent = {
  event_id: string;
  event_type?: string;
  received_at: string;
  dead_lettered_at: string;
  retry_count: number;
  last_error?: string;
  payload_json?: any;
};

export type AdminMe = {
  account_id: string;
  email: string;
  admin_role: string;
};

export type AuditLogEntry = {
  log_id?: string;
  timestamp: string;
  actor_account_id?: string;
  actor_email?: string;
  action: string;
  target_type?: string;
  target_id?: string;
  changes_json?: any;
};

export type AuditLogResponse = {
  entries: AuditLogEntry[];
  total?: number;
};

export type AccountSearchResult = {
  accounts: AccountDetail[];
  total: number;
};

// ─── Account endpoints ────────────────────────────────────────────────────────

export async function getAccount(id: string): Promise<AccountDetail> {
  return adminFetch<AccountDetail>(`/admin/accounts/${encodeURIComponent(id)}`);
}

export async function suspendAccount(id: string, reason: string, duration_hours?: number): Promise<any> {
  return adminFetch(`/admin/accounts/${encodeURIComponent(id)}/suspend`, {
    method: 'POST',
    body: JSON.stringify({ reason, ...(duration_hours != null ? { duration_hours } : {}) }),
  });
}

export async function unsuspendAccount(id: string, reason: string): Promise<any> {
  return adminFetch(`/admin/accounts/${encodeURIComponent(id)}/unsuspend`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function banAccount(id: string, reason: string): Promise<any> {
  return adminFetch(`/admin/accounts/${encodeURIComponent(id)}/ban`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function warnAccount(id: string, reason: string, source_report_id?: string): Promise<any> {
  return adminFetch(`/admin/accounts/${encodeURIComponent(id)}/warn`, {
    method: 'POST',
    body: JSON.stringify({ reason, ...(source_report_id ? { source_report_id } : {}) }),
  });
}

// ─── Report endpoints ─────────────────────────────────────────────────────────

export async function listReports(params: {
  status?: string;
  limit: number;
  offset: number;
}): Promise<ReportListResponse> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  qs.set('limit', String(params.limit));
  qs.set('offset', String(params.offset));
  return adminFetch<ReportListResponse>(`/admin/reports?${qs}`);
}

export async function resolveReport(id: string, action: string, reason: string): Promise<any> {
  return adminFetch(`/admin/reports/${encodeURIComponent(id)}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ action, reason }),
  });
}

// ─── Shard health endpoints (Sprint 24.1 backend exposed) ────────────────────
//
// Sprint 63: backend returns { shards: [...], count: n }. Accept both shapes so
// older / mocked endpoints that return a bare array still work.

export async function listShardHealth(): Promise<ShardHealthSnapshot[]> {
  const result = await adminFetch<
    ShardHealthSnapshot[] | { shards: ShardHealthSnapshot[]; count?: number }
  >('/admin/shard-health');
  return adaptShardHealthResponse<ShardHealthSnapshot>(result);
}

// ─── Stripe dead-letter endpoints (Sprint 24.1 backend exposed) ──────────────
//
// Sprint 63: backend returns { dead_lettered_events: [...], count: n }.
// Accept both shapes for forward/backward compatibility.

export async function listDeadLetters(): Promise<DeadLetterEvent[]> {
  const result = await adminFetch<
    DeadLetterEvent[] | { dead_lettered_events: DeadLetterEvent[]; count?: number }
  >('/admin/stripe/dead-letters');
  return adaptDeadLettersResponse<DeadLetterEvent>(result);
}

export async function retryDeadLetter(event_id: string): Promise<any> {
  return adminFetch(`/admin/stripe/dead-letters/${encodeURIComponent(event_id)}/retry`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function dismissDeadLetter(event_id: string): Promise<any> {
  return adminFetch(`/admin/stripe/dead-letters/${encodeURIComponent(event_id)}/dismiss`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ─── Identity endpoint (Sprint 24.4) ──────────────────────────────────────────

/**
 * GET /admin/me — returns the calling admin's account_id, email, and admin_role.
 * The role is cached to localStorage so UI gating doesn't require a round-trip
 * on every render. Cache is invalidated on logout (clearCredentials).
 *
 * Sprint 24.1 backend ticket must expose this endpoint.
 */
export async function getMe(): Promise<AdminMe> {
  const me = await adminFetch<AdminMe>('/admin/me');
  setCachedRole(me.admin_role ?? '');
  return me;
}

// ─── Account search by email (Sprint 24.4) ────────────────────────────────────

/**
 * GET /admin/accounts?email=<email>
 *
 * Sprint 24.1 backend exposes the email query param on the accounts list
 * endpoint. The current backend (findAdminAccountByEmail) returns a single
 * AccountDetail object when there is a match and 404s when not found.
 *
 * Sprint 63: tolerate three shapes so the UI works regardless of how the
 * backend evolves:
 *   - AccountDetail (single object — current backend behaviour)
 *   - AccountDetail[] (future: bare array)
 *   - { accounts: AccountDetail[], total } (future: paginated wrapper)
 *
 * Returns [] when the backend 404s (no match) rather than throwing, so the UI
 * layer can render a friendly "no accounts found" message.
 */
export async function searchAccountsByEmail(email: string): Promise<AccountDetail[]> {
  const qs = new URLSearchParams({ email });
  try {
    const result = await adminFetch<AccountSearchResult | AccountDetail[] | AccountDetail>(
      `/admin/accounts?${qs}`,
    );
    return adaptAccountSearchResponse<AccountDetail>(result);
  } catch (err: any) {
    if (err instanceof AdminApiError && (err.status === 404 || err.code === 'account_not_found')) {
      return [];
    }
    throw err;
  }
}

// ─── Audit log endpoint (Sprint 24.4) ─────────────────────────────────────────

/**
 * GET /admin/audit-log?since=&action=&actor=
 * TODO (Sprint 24.1 backend): Expose audit log HTTP endpoint.
 * Until it lands this returns 404/501 and the page shows a stub banner.
 */
export async function listAuditLog(params: {
  since?: string;
  action?: string;
  actor?: string;
  limit?: number;
  offset?: number;
}): Promise<AuditLogResponse> {
  const qs = new URLSearchParams();
  if (params.since)  qs.set('since',  params.since);
  if (params.action) qs.set('action', params.action);
  if (params.actor)  qs.set('actor',  params.actor);
  if (params.limit != null)  qs.set('limit',  String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  return adminFetch<AuditLogResponse>(`/admin/audit-log?${qs}`);
}

// ─── Audit log SSE stream (Sprint 26.3) ───────────────────────────────────────

/**
 * GET /admin/audit-log/stream  (SSE; moderator+)
 *
 * Sprint 26.1/27 backend TODO: Add SSE endpoint that emits audit_log rows as
 * they are written. Until that endpoint is live, openAuditLogStream() will
 * throw/return an error and AuditLogLive falls back to 5-second polling.
 *
 * Expected SSE event format:
 *   event: audit_entry
 *   data: { log_id, timestamp, actor_account_id, actor_email, action,
 *            target_type, target_id, actor_role, changes_json }
 *
 * This function returns an EventSource whose caller is responsible for closing.
 * The Authorization header cannot be set on native EventSource, so the token
 * is passed as a query param (server must accept ?token= for this endpoint).
 */
export function openAuditLogStream(): EventSource {
  const token = getToken();
  const url = `${API_BASE}/admin/audit-log/stream?token=${encodeURIComponent(token)}`;
  return new EventSource(url);
}

// ─── Welcome-Back season stats (Sprint 20.3 backend, Sprint 63 UI) ───────────

export type WelcomeBackDistributionBucket = {
  bucket: string;
  count: number;
};

export type WelcomeBackSeasonStats = {
  season_id: string;
  total_accounts: number;
  tier_a_count: number;
  tier_b_count: number;
  average_final_score: number | null;
  distribution: WelcomeBackDistributionBucket[];
};

/**
 * GET /admin/welcome-back/stats?season=N — aggregated returning-player stats
 * for a given season. Used to tune the Tier B threshold (score > 500).
 *
 * Backend: server/src/admin/admin-routes.mjs ADMIN_WELCOME_BACK_STATS
 * Service: getWelcomeBackSeasonStats() in admin-service.mjs
 */
export async function getWelcomeBackStats(season: number): Promise<WelcomeBackSeasonStats> {
  const qs = new URLSearchParams({ season: String(season) });
  return adminFetch<WelcomeBackSeasonStats>(`/admin/welcome-back/stats?${qs}`);
}

// ─── Abuse signals admin queue (Sprint 36 D3 backend, Sprint 63 UI) ──────────

export type AbuseSignalSeverity = 'low' | 'medium' | 'high';
export type AbuseSignalDisposition = 'dismiss' | 'warn' | 'throttle' | 'suspend';

export type AbuseSignal = {
  signal_id: string;
  gang_id: string;
  signal_type: string;
  severity: AbuseSignalSeverity;
  details?: Record<string, any>;
  detected_at: string;
  reviewed_at?: string | null;
  reviewer_account_id?: string | null;
  disposition?: AbuseSignalDisposition | null;
  review_notes?: string | null;
};

export type AbuseSignalListResponse = {
  signals: AbuseSignal[];
  count: number;
};

/**
 * GET /admin/abuse-signals?status=open|reviewed|all&severity=…&signal_type=…
 *
 * Backend: server/src/admin/admin-routes.mjs ADMIN_ABUSE_SIGNALS_LIST
 * Service: listOpenSignals() in abuse-detection-service.mjs
 */
export async function listAbuseSignals(params: {
  status?: 'open' | 'reviewed' | 'all';
  severity?: AbuseSignalSeverity;
  signal_type?: string;
  limit?: number;
  offset?: number;
}): Promise<AbuseSignal[]> {
  const qs = new URLSearchParams();
  if (params.status)      qs.set('status', params.status);
  if (params.severity)    qs.set('severity', params.severity);
  if (params.signal_type) qs.set('signal_type', params.signal_type);
  if (params.limit  != null) qs.set('limit',  String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const result = await adminFetch<AbuseSignalListResponse | AbuseSignal[]>(
    `/admin/abuse-signals?${qs}`,
  );
  return adaptAbuseSignalsResponse<AbuseSignal>(result);
}

/**
 * POST /admin/abuse-signals/:signalId/review — mark a signal reviewed with a
 * disposition and optional notes.
 *
 * Backend: server/src/admin/admin-routes.mjs ADMIN_ABUSE_SIGNAL_REVIEW
 * Service: reviewSignal() in abuse-detection-service.mjs
 */
export async function reviewAbuseSignal(
  signalId: string,
  disposition: AbuseSignalDisposition,
  notes?: string,
): Promise<AbuseSignal> {
  return adminFetch<AbuseSignal>(
    `/admin/abuse-signals/${encodeURIComponent(signalId)}/review`,
    {
      method: 'POST',
      body: JSON.stringify({ disposition, ...(notes ? { notes } : {}) }),
    },
  );
}

// ─── Sprint 71: GDPR delete review queue ──────────────────────────────────────

export type GdprDeleteRequest = {
  id: string;
  account_id: string;
  requested_at: string;
  scheduled_hard_delete_at: string;
  processed_at: string | null;
  status: 'pending' | 'cancelled' | 'completed';
};

export type GdprDeleteRequestListResponse = {
  requests: GdprDeleteRequest[];
  count: number;
};

/** GET /admin/gdpr-delete-requests — list pending GDPR Article 17 deletions. */
export async function listGdprDeleteRequests(): Promise<GdprDeleteRequest[]> {
  const result = await adminFetch<GdprDeleteRequestListResponse | GdprDeleteRequest[]>(
    '/admin/gdpr-delete-requests',
  );
  if (Array.isArray(result)) return result;
  return result.requests ?? [];
}

/** POST /admin/gdpr-delete-requests/:id/approve — force immediate hard-delete. */
export async function approveGdprDeleteRequest(
  requestId: string,
  reason: string,
): Promise<{ ok: true; account_id: string; hard_deleted_at: string }> {
  return adminFetch(
    `/admin/gdpr-delete-requests/${encodeURIComponent(requestId)}/approve`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

/** POST /admin/gdpr-delete-requests/:id/reject — clear soft-delete + cancel. */
export async function rejectGdprDeleteRequest(
  requestId: string,
  reason: string,
): Promise<{ ok: true; account_id: string }> {
  return adminFetch(
    `/admin/gdpr-delete-requests/${encodeURIComponent(requestId)}/reject`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

// ─── Sprint 71: IAP receipt manual-fulfillment ────────────────────────────────

export type IapReceiptPending = {
  id: string;
  account_id: string;
  platform: 'apple' | 'google';
  transaction_id: string;
  sku_id: string;
  amount_cents: number | null;
  currency: string | null;
  validated_at: string | null;
  status: 'pending' | 'validated';
};

export type IapReceiptPendingListResponse = {
  receipts: IapReceiptPending[];
  count: number;
};

/** GET /admin/iap-receipts/pending — list stuck IAP receipts awaiting fulfillment. */
export async function listPendingIapReceipts(): Promise<IapReceiptPending[]> {
  const result = await adminFetch<IapReceiptPendingListResponse | IapReceiptPending[]>(
    '/admin/iap-receipts/pending',
  );
  if (Array.isArray(result)) return result;
  return result.receipts ?? [];
}

/** POST /admin/iap-receipts/:id/manually-fulfill — admin override-fulfill. */
export async function manuallyFulfillIapReceipt(
  receiptId: string,
  reason: string,
): Promise<{ ok: true; account_id: string; sku_id: string }> {
  return adminFetch(
    `/admin/iap-receipts/${encodeURIComponent(receiptId)}/manually-fulfill`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

// ─── Sprint 71: Chat moderation queue ─────────────────────────────────────────

export type ChatReport = {
  id: string;
  reporter_account_id: string;
  reported_account_id: string;
  category: 'chat';
  reason: string;
  context_json?: {
    message_id?: string;
    channel_type?: 'syndicate' | 'district' | 'dm';
    channel_id?: string;
    body_snippet?: string;
  };
  status: 'open' | 'under_review' | 'actioned' | 'dismissed';
  created_at: string;
  resolved_at?: string | null;
};

export type ChatReportListResponse = {
  reports: ChatReport[];
  count: number;
};

export type ChatModerationAction =
  | 'dismiss'
  | 'delete-message'
  | 'warn-user'
  | 'mute-user'
  | 'ban-user';

/** GET /admin/chat-reports/queue — open chat reports awaiting moderation. */
export async function listChatReports(): Promise<ChatReport[]> {
  const result = await adminFetch<ChatReportListResponse | ChatReport[]>(
    '/admin/chat-reports/queue',
  );
  if (Array.isArray(result)) return result;
  return result.reports ?? [];
}

/** POST /admin/chat-reports/:id/action — apply moderation action. */
export async function actionChatReport(
  reportId: string,
  action: ChatModerationAction,
  reason: string,
  options?: { mute_minutes?: number },
): Promise<{ ok: true; report_id: string; status: string; action: ChatModerationAction }> {
  return adminFetch(
    `/admin/chat-reports/${encodeURIComponent(reportId)}/action`,
    {
      method: 'POST',
      body: JSON.stringify({
        action,
        reason,
        ...(options?.mute_minutes != null ? { mute_minutes: options.mute_minutes } : {}),
      }),
    },
  );
}
